import React, { useState } from 'react';
import { Plus, Trash2, ChevronRight, ChevronDown, Copy, ChevronsDownUp, ChevronsUpDown, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMoney } from '../../context/CurrencyContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { NumberStepper } from '../NumberStepper';
import { cn } from '../../lib/utils';

export function SaleBoqPanel({ computed, selectedId, onSelect, onQtyChange, onProfitChange, onAddItem, onDeleteItem, onDuplicateItem, readOnly = false }) {
  const { money } = useMoney();
  const items = computed.saleItems;
  // P4 — per-card collapse state (persists while on the Workspace page).
  const [collapsed, setCollapsed] = useState({});
  const allCollapsed = items.length > 0 && items.every((it) => collapsed[it.itemId]);
  const toggleAll = () => {
    if (allCollapsed) setCollapsed({});
    else setCollapsed(Object.fromEntries(items.map((it) => [it.itemId, true])));
  };
  const toggleOne = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  // A Sale line is "empty" when no Activity Master template has been applied to it —
  // since all Cost BOQ lines now come from activities, that means it would ship at zero cost.
  const hasActivity = (it) => (it.costBoq?.categories || []).some((c) => (c.lines || []).some((l) => l.activity));

  return (
    <TooltipProvider delayDuration={250}>
    <div className="flex h-full flex-col" data-testid="workspace-sale-boq-table">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <div className="font-display text-sm font-semibold">Sale BOQ</div>
          <div className="text-[11px] text-muted-foreground">Client-facing deliverable lines</div>
        </div>
        <div className="flex items-center gap-1.5">
          {items.length > 0 && (
            <button data-testid="sale-boq-collapse-all" onClick={toggleAll}
              className="flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-muted">
              {allCollapsed ? <ChevronsUpDown size={12} /> : <ChevronsDownUp size={12} />}
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
          )}
          <button data-testid="add-sale-item" onClick={onAddItem} disabled={readOnly} className={cn('grid h-7 w-7 place-items-center rounded-lg border hover:bg-muted', readOnly && 'pointer-events-none opacity-40')}><Plus size={15} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {items.map((it) => {
          const active = it.itemId === selectedId;
          const isCollapsed = !!collapsed[it.itemId];
          return (
            <motion.div key={it.itemId} layout
              data-testid={`sale-item-${it.itemNo}`}
              onClick={() => onSelect(it.itemId)}
              className={cn('cursor-pointer border-b px-4 py-3 transition-colors',
                active ? 'bg-[hsl(var(--teal)/0.1)] ring-1 ring-inset ring-[hsl(var(--teal)/0.35)]' : 'hover:bg-muted/50')}>
              {/* Row 1: collapse chevron + code + action icons */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <button data-testid={`sale-item-collapse-${it.itemNo}`} aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                    onClick={(e) => { e.stopPropagation(); toggleOne(it.itemId); }}
                    className="grid h-5 w-5 shrink-0 place-items-center rounded hover:bg-muted">
                    <ChevronDown size={13} className={cn('text-muted-foreground transition-transform', isCollapsed && '-rotate-90')} />
                  </button>
                  <span className="font-num text-xs font-semibold text-[hsl(var(--copper))]">{it.itemNo}</span>
                  {active && <ChevronRight size={12} className="shrink-0 text-[hsl(var(--teal))]" />}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {!readOnly && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button data-testid={`duplicate-sale-item-${it.itemNo}`} onClick={(e) => { e.stopPropagation(); onDuplicateItem(it.itemId); }}
                            className="rounded p-1 text-muted-foreground hover:text-[hsl(var(--teal))]"><Copy size={13} /></button>
                        </TooltipTrigger>
                        <TooltipContent side="left">Duplicate this Sale line (full copy)</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button data-testid={`delete-sale-item-${it.itemNo}`} onClick={(e) => { e.stopPropagation(); onDeleteItem(it.itemId); }}
                            className="rounded p-1 text-muted-foreground hover:text-[hsl(var(--danger))]"><Trash2 size={13} /></button>
                        </TooltipTrigger>
                        <TooltipContent side="left">Delete this Sale line</TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>

              {/* Row 2: description — always visible (1 line when collapsed, up to 3 expanded) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={cn('mt-1.5 text-xs leading-snug text-foreground', isCollapsed ? 'line-clamp-1' : 'line-clamp-3')}>{it.description || 'Untitled item'}</div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">{it.description || 'Untitled item'}</TooltipContent>
              </Tooltip>

              {/* Empty-cost guard: no activity applied → this line would ship at zero cost */}
              {!hasActivity(it) && (
                <div data-testid={`no-activity-warning-${it.itemNo}`}
                  title="No activity applied — apply an activity in the Cost BOQ so this line has a cost"
                  className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-[hsl(var(--warning)/0.45)] bg-[hsl(var(--warning)/0.12)] px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--warning))]">
                  <AlertTriangle size={10} />No activity applied — zero cost
                </div>
              )}

              {isCollapsed ? (
                /* P6 — collapsed summary: shared NumberStepper for QTY, Profit as % only,
                   tabular 3-column alignment (Qty | Profit % | Sell). */
                <div className="mt-2 grid grid-cols-3 items-center gap-2 border-t pt-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Qty</span>
                    <NumberStepper value={it.qty} step={1} decimals={0} min={1} disabled={readOnly}
                      data-testid={`sale-qty-${it.itemNo}`} inputClassName="w-10"
                      onChange={(v) => onQtyChange(it.itemId, v)} />
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Profit</span>
                    <span data-testid={`sale-profit-pct-${it.itemNo}`} className="font-num mt-0.5 text-[11px] tabular-nums text-[hsl(var(--teal))]">{((it.profitPct || 0) * 100).toFixed(2)}%</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Sell</span>
                    <span className="money mt-0.5 text-[11px] tabular-nums font-semibold text-[hsl(var(--copper))]">{money(it.sellPrice, 0)}</span>
                  </div>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    {/* Row 3: qty */}
                    <div className="mt-2.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Qty</label>
                      <NumberStepper value={it.qty} step={1} decimals={0} min={1} disabled={readOnly}
                        data-testid={`sale-qty-${it.itemNo}`} inputClassName="w-12"
                        onChange={(v) => onQtyChange(it.itemId, v)} />
                      <span className="text-[10px] text-muted-foreground">{it.unit}</span>
                    </div>
                    {/* Row 3b: per-line Profit % (P5) */}
                    <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Profit</label>
                      <NumberStepper value={Number(((it.profitPct || 0) * 100).toFixed(2))} step={0.25} decimals={2} min={0} max={100} suffix="%" disabled={readOnly}
                        data-testid={`sale-profit-${it.itemNo}`} inputClassName="w-14"
                        onChange={(v) => onProfitChange && onProfitChange(it.itemId, v)} />
                    </div>
                    {/* Row 4: Cost / Unit / Sell — three even columns, numbers right-aligned under labels */}
                    <div className="mt-2.5 grid grid-cols-3 gap-2 border-t pt-2">
                      <div className="flex flex-col items-end"><div className="text-[9px] uppercase tracking-wide text-muted-foreground">Cost</div><div className="money mt-0.5 text-[11px]">{money(it.totalCost, 0)}</div></div>
                      <div className="flex flex-col items-end"><div className="text-[9px] uppercase tracking-wide text-muted-foreground">Unit</div><div className="money mt-0.5 text-[11px]">{money(it.effectiveUnitCost, 0)}</div></div>
                      <div className="flex flex-col items-end"><div className="text-[9px] uppercase tracking-wide text-muted-foreground">Sell</div><div className="money mt-0.5 text-[11px] font-semibold text-[hsl(var(--copper))]">{money(it.sellPrice, 0)}</div></div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}
            </motion.div>
          );
        })}
        {!items.length && <div className="p-6 text-center text-xs text-muted-foreground">No sale items. Click + to add one.</div>}
      </div>
      <div className="border-t px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Tender Direct Cost</span>
          <span className="money text-sm font-semibold">{money(computed.directCost)}</span>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
